import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';
import { LOAD, LOAD_END } from '../../../../../../../store/actions/actionsTypes';
import PackageBoxIcon from '../../../../../../generic/icons/IconLib/PackageBoxIcon';
import ColumModal from '../../../../../../generic/Layouts/ColumModal';
import { ContextModal } from '../../../../../../modules/modal/modalContext';
import { AccentButton } from '../../../../../../generic/ui/Button';
import { FormFooter } from '../../../../../../generic/ui/FormLayout';
import KbdChipButton from '../../../../../../generic/ui/KbdChipButton/KbdChipButton';
import { SearchInput } from '../../../../../../generic/ui/SearchInput';
import { addIDForTheList } from '../../../../../../generic/utils/addUIDForTheList';
import { IArizotLine } from '../../../../../../modules/API/Packages/models/arizotLine';
import { IOrderPackageTitle } from '../../../../../../modules/API/Packages/models/createPackageOrderModel';
import { Packages } from '../../../../../../modules/API/Packages/packages';
import RemarkModal from '../../../arizot/View/RemarkModal';
import type { IRemarkData } from '../../../arizot/View/ArizotViewMain';
import PackagesPickList, { IPackRow } from './PackagesPickList';
import TeudaCreatePanel from './TeudaCreatePanel';

interface Props {
    searchParam: string;
    closeFunc: () => void;
    isAllowTeuda?: boolean;
}

/**
 * Просмотр и правка заказа упаковки. Копия логики arizot/View/ArizotViewMain
 * под новый стандарт UI — оригинал остаётся, его используют другие экраны.
 */
const PackageOrderView: React.FC<Props> = ({ searchParam, closeFunc, isAllowTeuda }) => {
    const [t] = useTranslation(['packageOrder', 'buttons']);
    const dispatch = useDispatch();
    const [modal, setModal] = useContext(ContextModal);

    const [arizotLines, setArizotLines] = useState<IArizotLine[]>();
    const [title, setTitle] = useState<IOrderPackageTitle>();
    const [input, setInput] = useState<string>('');
    const [isEditActive, setIsEditActive] = useState<boolean>(false);
    const [isOpenTeudaWindow, setIsOpenTeudaWindow] = useState<boolean>(false);
    const [isOpenRemarkWindow, setIsOpenRemarkWindow] = useState<boolean>(false);
    const [remarkData, setRemarkData] = useState<IRemarkData | null>(null);

    const notify = (value: string, mood: boolean) => {
        setModal({ open: true, value, mood });
        setTimeout(() => setModal({ ...modal, open: false }), 3000);
    };

    const handleControlTeudaModal = () => setIsOpenTeudaWindow((prev) => !prev);

    const getLines = async () => {
        dispatch({ type: LOAD });
        try {
            const res = await Packages.getArizotLinesWithTitle(searchParam);
            const { Packs_Pallets, ...rest } = res;
            setArizotLines(addIDForTheList(Packs_Pallets).map((e) => ({ ...e, QtyTeuda: e.Qty })));
            setTitle(rest);
        } catch (err) {
        } finally {
            dispatch({ type: LOAD_END });
        }
    };

    useEffect(() => {
        getLines();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchParam]);

    const editOrderPackage = async () => {
        if (!arizotLines) return;
        dispatch({ type: LOAD });
        const mapped = arizotLines.map((e) => {
            const { id, QtyTeuda, ...rest } = e;
            return rest;
        });
        try {
            await Packages.putPackageLines(searchParam, { Packs_Pallets: mapped });
            notify('הזמנה התנהלה בהצלחה', true);
            closeFunc();
        } catch (err) {
        } finally {
            dispatch({ type: LOAD_END });
        }
    };

    const sendEmail = async (teudaNum: number) => {
        try {
            await Packages.sendEmail(teudaNum);
            notify(`דוא"ל נשלח בהצלחה`, true);
        } catch (err: any) {
            notify(err?.response?.status === 405 ? 'אין מייל' : 'משהו השתבש', false);
        }
    };

    const createTeuda = async (sendMail: boolean) => {
        if (!arizotLines) return;
        if (arizotLines.some((e) => (e.QtyTeuda || 0) > e.Qty)) {
            notify('לא ניתן לתת מעבר להזמה', false);
            return;
        }
        const postData = {
            PO_Num: +searchParam,
            ClientID_Recipient: title?.ClientID_Recipient,
            ClientID_Sender: title?.ClientID_Sender,
            PD_Date: title?.PO_Date,
            Vehicle_Num: `${title?.Vehicle_Num}`,
            Driver_Name: title?.Driver_Name,
            Remarks: title?.Remarks,
            Packs_Pallets: arizotLines.map((pack) => {
                pack.Qty = pack.QtyTeuda || 0;
                const { QtyTeuda, id, RecID, PD_ID, Remark, Description, Season, ...rest } = pack;
                return rest;
            }),
        };
        try {
            const res = await Packages.createTeuda(postData);
            notify('תעודה נוצרה בהצלחה', true);
            handleControlTeudaModal();
            if (sendMail) await sendEmail(res.PD_Num);
            closeFunc();
        } catch (err) {
            notify('משהו השתבש', false);
        }
    };

    const patchState = (qty: number, id: string) => {
        if (!arizotLines) return;
        const newState = [...arizotLines];
        const found = newState.find((e) => e.id === id);
        if (!found) return;
        setIsEditActive(true);
        newState[newState.indexOf(found)].Qty = qty;
        setArizotLines(newState);
    };

    const patchStateTeuda = (qty: number, id: string) => {
        if (!arizotLines) return;
        const newState = [...arizotLines];
        const found = newState.find((e) => e.id === id);
        if (!found) return;
        newState[newState.indexOf(found)].QtyTeuda = qty;
        setArizotLines(newState);
        if (arizotLines.some((e) => (e?.QtyTeuda || 0) > e.Qty)) {
            notify('לא ניתן לתת מעבר להזמנה', false);
        }
    };

    const selectRemarkLine = (row: IPackRow) => {
        setRemarkData({ title: row.name, body: row.remark?.trim() ?? '', id: row.id });
        setIsOpenRemarkWindow(true);
    };

    const handleRemarkModal = () => {
        setRemarkData(null);
        setIsOpenRemarkWindow(false);
    };

    const rows: IPackRow[] = useMemo(
        () =>
            (arizotLines ?? [])
                .filter((item) =>
                    item.Description.trim().toLocaleLowerCase().includes(input.toLocaleLowerCase()),
                )
                .map((line) => ({
                    id: line.id ?? `${line.PackID}`,
                    name: line.Description?.trim() ?? '',
                    type: line.Pack_Pallet ?? '',
                    qty: line.Qty,
                    remark: line.Remark,
                })),
        [arizotLines, input],
    );

    const isOrderOpen = title?.PO_Status === 0;

    return (
        <Panel>
            {isAllowTeuda && isOpenTeudaWindow && arizotLines && (
                <ColumModal
                    controlFunc={handleControlTeudaModal}
                    Children={TeudaCreatePanel}
                    childrenProps={{
                        list: arizotLines,
                        editFunc: patchStateTeuda,
                        saveFunc: createTeuda,
                    }}
                    title={`הזמנה אריזה #${searchParam}`}
                />
            )}

            {isOpenRemarkWindow && (
                <ColumModal
                    title={remarkData?.title || ''}
                    Children={RemarkModal}
                    controlFunc={handleRemarkModal}
                    childrenProps={{ data: remarkData, close: handleRemarkModal }}
                />
            )}

            <Toolbar>
                <SearchInput
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    placeholder={t('packageOrder_ViewWindow_title', { defID: searchParam })}
                />
                {isAllowTeuda && !title?.PD_Num && (
                    <KbdChipButton
                        type='button'
                        aria-label='ליצור תעודה'
                        title='ליצור תעודה'
                        onClick={handleControlTeudaModal}
                    >
                        <PackageBoxIcon />
                    </KbdChipButton>
                )}
            </Toolbar>

            <PackagesPickList
                rows={rows}
                onQtyChange={isOrderOpen ? patchState : undefined}
                onRemark={selectRemarkLine}
                readOnly={!isOrderOpen}
            />

            <FormFooter>
                <AccentButton type='button' variant='ghost' onClick={closeFunc} fullWidth={false}>
                    {t('buttons:cancel')}
                </AccentButton>
                {isEditActive && isOrderOpen && (
                    <AccentButton type='button' onClick={editOrderPackage} fullWidth={false}>
                        {t('buttons:save')}
                    </AccentButton>
                )}
            </FormFooter>
        </Panel>
    );
};

export default PackageOrderView;

const Panel = styled.div`
    width: 100%;
    height: 100%;
    min-height: 0;
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
    direction: rtl;
`;

const Toolbar = styled.div`
    display: flex;
    align-items: center;
    gap: 0.5rem;
    flex-shrink: 0;
`;
